import {
  Alert,
  Avatar,
  AvatarFallback,
  LanguageSwitcher,
  Logo,
  ThemeToggle,
  UnreadDot,
} from '@zuruflex/ui';
import {
  BadgeCheck,
  CalendarDays,
  Car,
  LayoutGrid,
  LogOut,
  MessageSquare,
  ScrollText,
  Settings,
  Star,
  TicketPercent,
} from 'lucide-react';
import { useUnreadMessages } from '@zuruflex/api-client/react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { SupportProvider } from '../lib/support.js';

interface NavItem {
  to: string;
  key: string;
  icon: typeof LayoutGrid;
  /** Only the index route needs exact matching. */
  end?: boolean;
}

interface NavGroup {
  /** Omitted for the overview, which is home rather than a section. */
  key?: string;
  items: NavItem[];
}

/*
 * Grouped the way the admin console is, by the job a partner opens the
 * portal to do: run today's hires, win more of them, or look after the
 * account itself. Most-visited sections first.
 */
const NAV: NavGroup[] = [
  {
    items: [{ to: '/', key: 'nav.overview', end: true, icon: LayoutGrid }],
  },
  {
    key: 'nav.group.rentals',
    items: [
      { to: '/bookings', key: 'nav.bookings', icon: CalendarDays },
      { to: '/fleet', key: 'nav.fleet', icon: Car },
      { to: '/inbox', key: 'nav.inbox', icon: MessageSquare },
    ],
  },
  {
    key: 'nav.group.growth',
    items: [
      { to: '/promotions', key: 'nav.promotions', icon: TicketPercent },
      { to: '/reviews', key: 'nav.reviews', icon: Star },
    ],
  },
  {
    key: 'nav.group.account',
    items: [
      { to: '/verification', key: 'nav.verification', icon: BadgeCheck },
      { to: '/terms', key: 'nav.terms', icon: ScrollText },
      { to: '/settings', key: 'nav.settings', icon: Settings },
    ],
  },
];

/** The mobile bar has no room for headings, so it takes the same order flat. */
const NAV_ITEMS = NAV.flatMap((group) => group.items);

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

/**
 * The same curved black sidebar as the operations dashboard, so the two
 * feel like one product — but a shorter one. A partner has four things to
 * do here, and a portal that mimics the full admin nav would imply they can
 * do more.
 */
export function AppLayout() {
  const { t, i18n } = useTranslation(['partner', 'common']);
  const { user, partner, signOut } = useAuth();
  const unread = useUnreadMessages(api).data ?? 0;
  // Both of these banners say, at more length and in its own words, what
  // the verification page already says at the top of itself — one of them
  // as a link to the page you are on. They are the only prompt there is
  // everywhere else, and noise on the one page that is about them.
  const onVerification = useLocation().pathname === '/verification';

  return (
    <div className="flex min-h-screen bg-surface">
      {/*
        The sidebar is a black band in either theme, so it carries `dark`
        itself: everything nested in it — the switchers, the sign-out row —
        then resolves against dark tokens no matter what the page is set to.

        It runs to the top, bottom and left of the glass and curves only on
        the edge that faces the work surface — the one edge where the radius
        has a surface to be read against.
      */}
      <aside className="dark sticky top-0 hidden h-screen w-60 shrink-0 flex-col gap-2 overflow-y-auto rounded-r-panel bg-ink-950 p-3 md:flex">
        <div className="shrink-0 px-3 pb-2 pt-4">
          <Logo className="h-7" />
          <p className="mt-2 text-[10px] font-bold uppercase tracking-[0.2em] text-text-muted">
            {t('nav.section')}
          </p>
        </div>

        <nav className="flex flex-1 flex-col gap-5">
          {NAV.map((group, index) => (
            <div key={group.key ?? index} className="flex flex-col gap-1">
              {group.key ? (
                <p className="px-4 pb-1 text-[10px] font-bold uppercase tracking-[0.2em] text-text-muted">
                  {t(group.key)}
                </p>
              ) : null}
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    [
                      'flex items-center gap-3 rounded-pill px-4 py-2.5 text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-accent text-accent-text'
                        : 'text-text-muted hover:bg-surface-hover hover:text-text',
                    ].join(' ')
                  }
                >
                  <item.icon className="size-4" />
                  <span className="flex-1">{t(item.key)}</span>
                  {item.to === '/inbox' ? (
                    <UnreadDot
                      count={unread}
                      label={t('common:messaging.unread', { count: unread })}
                    />
                  ) : null}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="shrink-0 space-y-1">
          <LanguageSwitcher
            value={i18n.language}
            onChange={(locale) => void i18n.changeLanguage(locale)}
            label={t('common:field.language')}
            className="w-full justify-start px-4 text-text-muted hover:bg-surface-hover hover:text-text"
          />
          <ThemeToggle
            variant="full"
            className="w-full justify-start px-4 text-text-muted hover:bg-surface-hover hover:text-text"
          />
          <button
            type="button"
            onClick={() => void signOut()}
            className="group flex w-full items-center gap-3 rounded-pill p-2 pr-4 text-left transition-colors hover:bg-surface-hover"
          >
            <Avatar className="size-9">
              <AvatarFallback>{initials(partner?.displayName ?? '?')}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-semibold text-text">{partner?.displayName}</p>
              <p className="truncate text-[11px] text-text-muted">{user?.email}</p>
            </div>
            <LogOut
              className="size-4 shrink-0 text-text-muted group-hover:text-text"
              aria-label={t('common:action.signOut')}
            />
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-40 flex items-center justify-between border-b border-border-default bg-surface px-4 py-3 md:hidden">
          <Logo className="h-6" />
          <div className="flex items-center gap-1">
            <ThemeToggle />
            <LanguageSwitcher
              value={i18n.language}
              onChange={(locale) => void i18n.changeLanguage(locale)}
              variant="compact"
              label={t('common:field.language')}
            />
          </div>
        </header>

        <main className="flex w-full min-w-0 flex-1 flex-col px-4 py-6 pb-28 md:px-6 md:py-8 md:pb-8">
          {/*
            A suspension is shown on every page, not just the overview.
            Someone who lands straight on their fleet and finds nothing live
            deserves to know why without hunting for it.
          */}
          {onVerification ? null : partner?.status === 'SUSPENDED' ? (
            <Alert variant="danger" className="mb-6">
              <span className="font-semibold">{t('overview.suspendedTitle')}</span>
              {partner.statusReason ? <> — {partner.statusReason}</> : null}
            </Alert>
          ) : partner?.status === 'PENDING' ? (
            /*
             * A partner still filling the form in and one waiting on us
             * need opposite things said to them. Telling the first to sit
             * tight is how an account sits at PENDING for a fortnight with
             * nobody at either end expecting to act.
             */
            <Alert variant={partner.submittedAt ? 'info' : 'warning'} className="mb-6">
              <span className="font-semibold">
                {t(partner.submittedAt ? 'overview.pendingTitle' : 'overview.unverifiedTitle')}
              </span>{' '}
              {t(partner.submittedAt ? 'overview.pendingBody' : 'overview.unverifiedBody')}{' '}
              {partner.submittedAt ? null : (
                <Link to="/verification" className="underline underline-offset-2">
                  {t('nav.verification')}
                </Link>
              )}
            </Alert>
          ) : null}

          {/*
            One Ask Zuruflex dialog for the whole portal, so any page under the
            outlet can open it with the hire or the car it is already
            showing — the difference between asking about a booking and
            asking about a booking somebody has to go and find first.

            Around the outlet rather than the whole layout because the
            dialog portals to `body` and nothing in the chrome opens it, so
            wrapping the sidebar and the mobile bar would buy nothing.
          */}
          <SupportProvider>
            <Outlet />
          </SupportProvider>
        </main>
      </div>

      {/*
        Mobile nav, because a partner checking a handover is on a phone.
        It floats clear of the bottom edge as a pill rather than sitting on
        the edge as a bar, which is the same shape the sidebar takes above
        the fold on a desktop.
      */}
      <nav className="fixed inset-x-3 bottom-3 z-40 flex gap-1 rounded-pill border border-border-default bg-surface/90 p-1.5 shadow-lift backdrop-blur md:hidden">
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              [
                'flex flex-1 flex-col items-center gap-1 rounded-pill py-2 text-[10px] font-medium transition-colors',
                isActive ? 'bg-accent-soft text-accent-soft-text' : 'text-text-muted',
              ].join(' ')
            }
          >
            <span className="relative">
              <item.icon className="size-5" />
              {item.to === '/inbox' ? (
                <UnreadDot
                  count={unread}
                  label={t('common:messaging.unread', { count: unread })}
                  className="absolute -right-2 -top-1"
                />
              ) : null}
            </span>
            {/* Nine items now, so the label goes below the narrowest phones
                rather than every item shrinking to fit the longest word. */}
            <span className="hidden sm:inline">{t(item.key)}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
